import React, { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { apiService } from '../../services/api';
import LoadingSpinner from '../ui/LoadingSpinner';

interface AuthWrapperProps {
  children: React.ReactNode;
}

const AuthWrapper: React.FC<AuthWrapperProps> = ({ children }) => {
  const auth0Hook = useAuth0();
  const { 
    isLoading, 
    isAuthenticated, 
    loginWithRedirect, 
    getAccessTokenSilently,
    error 
  } = auth0Hook;

  // Make Auth0 instance available globally for debugging
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).__auth0_client__ = {
        ...auth0Hook,
        getTokenSilently: getAccessTokenSilently
      };
      console.log('🔧 Auth0 client exposed to window.__auth0_client__ for debugging');
    }
  }, [auth0Hook, getAccessTokenSilently]);

  useEffect(() => {
    const setAuthToken = async () => {
      console.group('🔐 AUTH WRAPPER DEBUG');
      console.log('📊 Auth status:', { isAuthenticated, isLoading });
      console.log('🌐 Auth0 Audience:', process.env.REACT_APP_AUTH0_AUDIENCE);
      
      if (isAuthenticated) {
        try {
          console.log('🔑 Getting access token from Auth0...');
          const token = await getAccessTokenSilently({
            authorizationParams: {
              audience: process.env.REACT_APP_AUTH0_AUDIENCE,
              scope: 'openid profile email read:games download:registrations'
            }
          });
          
          console.log('✅ Token received from Auth0');
          console.log('📏 Token length:', token.length);
          console.log('🔤 Token preview:', token.substring(0, 50) + '...');
          
          // Set token in API service
          apiService.setAuthToken(token);
          console.log('✅ Token set in API service');
          
          // Debug the auth state after setting token
          setTimeout(() => {
            console.log('🔍 API service auth state:');
            apiService.debugAuthState();
          }, 100);
          
        } catch (error) {
          console.error('❌ Error getting access token:', error);
          if (error instanceof Error) {
            console.error('📋 Error details:', {
              message: error.message,
              name: error.name,
              stack: error.stack
            });
          } else {
            console.error('📋 Unknown error type:', error);
          }
        }
      } else {
        console.log('🚫 Not authenticated, removing auth token');
        apiService.removeAuthToken();
      }
      
      console.groupEnd();
    };

    setAuthToken();
  }, [isAuthenticated, getAccessTokenSilently, isLoading]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-900">
        <div className="text-center">
          <LoadingSpinner size="large" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">
            Loading application...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-900">
        <div className="text-center p-8 card max-w-md">
          <div className="text-red-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Authentication Error
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {error.message}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-900">
        <div className="text-center p-8 card max-w-md">
          <div className="mb-6">
            <svg className="w-16 h-16 mx-auto text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Games Admin
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Please sign in to access the admin dashboard
          </p>
          <button
            onClick={() => loginWithRedirect()}
            className="btn-primary w-full"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default AuthWrapper;
